import { Notification } from "react-notification-system";
import _ from "lodash";
import { error, info, success, warning, hide } from "react-notification-system-redux";
import NotificationContents, {
  NotificationOpts
} from "../components/NotificationContents/NotificationContents";
import moment from "moment";

// Type for actions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FunctionType = (...args: any[]) => any;
type ActionCreatorsMapObject = { [actionCreator: string]: FunctionType };
export type ActionsUnion<A extends ActionCreatorsMapObject> = ReturnType<A[keyof A]>;
interface Action<T extends string> {
  type: T;
}
export interface ActionWithPayload<T extends string, P> extends Action<T> {
  payload: P;
}

// Function for creating actions
export function createAction<T extends string>(type: T): Action<T>;
export function createAction<T extends string, P>(type: T, payload: P): ActionWithPayload<T, P>;
export function createAction<T extends string, P>(type: T, payload?: P): Api.AnyItem {
  return payload !== undefined ? { type, payload } : { type };
}

// Factory type for adding type checking
export const nameofFactory = <T>() => (name: keyof T): keyof T => name;

// Returns if user roles contain a role with name admin
export const isAdmin = (user: Api.User | undefined): boolean =>
  !!(user && user.roles.find(role => role.name === "admin"));

// Returns if user roles contain a role with name admin or write
export const isWrite = (user: Api.User | undefined): boolean =>
  !!(user && user.roles.find(role => role.name === "admin" || role.name === "write"));

export const hasRole = (user: Api.User | undefined, role: string): boolean =>
  !!(user && user.roles.find(r => r.name === role));

export const hasWriteAccess = (
  user: Api.User | undefined,
  domainGroup?: Api.GroupName | undefined | Api.GroupName[],
  allowAnyWrite?: boolean
): boolean => {
  if (allowAnyWrite) {
    return !!(user && user.roles.find(role => role.name.includes("write")));
  }
  if (domainGroup) {
    if (Array.isArray(domainGroup)) {
      let hasSomeAccess = false;
      domainGroup.forEach(dg => {
        hasSomeAccess =
          hasSomeAccess ||
          !!(user && user.roles.find(role => role.name === dg?.toLowerCase() + "-write"));
      });
      return hasSomeAccess;
    } else {
      return !!(
        user && user.roles.find(role => role.name === domainGroup?.toLowerCase() + "-write")
      );
    }
  }
  return !!(user && user.roles.find(role => role.name.includes("write")));
};

export const isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

function escapeRegExp(str: string): string {
  // see: https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Check if a given string (matchString) contains the substring (rule)
export const searchMatchRule = (matchString: string, rule: string): boolean => {
  matchString = matchString.toLowerCase().replace(/[\n\r]/g, "");
  rule = escapeRegExp(rule.toLowerCase());
  const firstChar = rule[0];
  const lastChar = rule[-1];
  if (firstChar !== "*") {
    rule = "*" + rule;
  }
  if (lastChar !== "*") {
    rule = rule + "*";
  }
  return new RegExp("^" + rule.split("*").join(".*") + "$").test(matchString);
};

/**
 * Returns an object that defines the contents of notification.
 * This is meant to be used with react-notification-system-redux.
 * @param {string} title Notification title
 * @param {string} [message] Notification message
 * @param {callback function} [onRemove] A callback function that will be called
 *        when the notification is about to be removed.
 * @returns {Notification}
 */
export const showSuccess = (message: string, opts?: NotificationOpts): Action<string> =>
  success(getNotificationObject("success", message, opts));

export const showInfo = (message: string, opts?: NotificationOpts): Action<string> =>
  info(getNotificationObject("info", message, opts));

export const showWarning = (message: string, opts?: NotificationOpts): Action<string> =>
  warning(getNotificationObject("warning", message, opts));

export const showError = (message: string, opts?: NotificationOpts): Action<string> =>
  error(getNotificationObject("error", message, opts));

export const hideNotification = (oid: number): Api.AnyItem => hide(oid);

export const getNotificationObject = (
  severity: string,
  message: string,
  opts?: NotificationOpts
): Notification => ({
  children: NotificationContents({ severity, message, opts }),
  position: "tc",
  autoDismiss: !opts ? 3 : opts.autoDismiss != undefined ? opts.autoDismiss : 3
});

export const parseUrlToName = (url: string): string => {
  const s = url
    .split("-")
    .map(word => {
      if (word === "io") {
        return "IO";
      }
      if (word === "cpus") {
        return "CPUs";
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
  return s;
};
export const parseNameToUrl = (url: string): string => {
  const s = url.replace(/\s/g, "-").toLowerCase();
  return s;
};

/**
 * Common function for converting numbers
 * @param num
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertBase = function (num: string): any {
  return {
    from: function (baseFrom: number) {
      return {
        to: function (baseTo: number) {
          return parseInt(num, baseFrom).toString(baseTo);
        }
      };
    }
  };
};

/**
 * Convert HEX number to 32 digit binary string
 * @param hexNo
 */
export const createBitMaskValue = (hexNo: string): string[] => {
  // Create static array of string consisting of 32 '0'
  const static32: string[] = new Array(32);
  static32.fill("0", 0, 32);

  if (hexNo === "" || hexNo === undefined) {
    return static32;
  }
  // Convert the HEX to Binary
  const originalBinary = convertBase(hexNo)
    .from(16)
    .to(2);

  // Convert String to Array
  const convertedBinaryArray = originalBinary.split("");

  // Calculate the length of converted binary
  const OriginLength = originalBinary.length;

  // Create the first part
  const firstPart = _.dropRight(static32, OriginLength);

  // Create dynamic 32 bit
  const dynamic32 = _.concat(firstPart, convertedBinaryArray);
  return dynamic32;
};

/**
 * Convert HEX mask value to a human-readable, space-separated 8-bit grouped binary string
 * (e.g. "00000011 10000000 00000000 00000000"), matching how the Binary component displays it.
 * @param hexNo
 */
export const formatBitMaskString = (hexNo: string): string => {
  const bits = createBitMaskValue(hexNo);
  const groups: string[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    groups.push(bits.slice(i, i + 8).join(""));
  }
  return groups.join(" ");
};

/**
 * Update the mask value based on the new first and last index
 * @param arr
 * @param first
 * @param last
 */
export const updateMask = (arr: string[], first: number, last: number): string[] => {
  const newMask = arr.fill("1", first, last);
  return newMask;
};

/**
 * Find the index for first and last 1
 * @param binaryArray
 */
export const findIndexes = (binaryArray: string[]): { first: number; last: number } => {
  const firstIndex = _.findIndex(binaryArray, item => item === "1");
  const lastIndex = _.findLastIndex(binaryArray, item => item === "1");
  return {
    first: firstIndex,
    last: lastIndex
  };
};

/**
 * Extract the mask value based on the first and last index
 * @param arr
 * @param first
 * @param last
 */
export const calculateMaxValue = (arr: string[], first: number, last: number): string | number => {
  if (first === last && first > 0 && last > 0) {
    return "1";
  } else if (first === last && first < 0 && last < 0) {
    return "0";
  } else if (first > last) {
    return "0";
  } else {
    const maskValue = _.slice(arr, first, last + 1);
    const maskedBinary = _.join(maskValue, "");
    const maxValue = convertBase(maskedBinary)
      .from(2)
      .to(10);
    return maxValue;
  }
};

export const createHex = (binaryArray: string[], first: number): string => {
  const validBinaryArray = _.slice(binaryArray, first);
  const binaryStr = _.join(validBinaryArray, "");
  const hexVal = convertBase(binaryStr)
    .from(2)
    .to(16);
  return `0x${hexVal}`;
};

export const isBase64Strict = (value: unknown): boolean => {
  if (value == null) return false;

  // coerce to string and trim
  let s = String(value).trim();

  // reject hex-looking inputs explicitly
  if (/^0x/i.test(s)) return false;

  // normalize URL-safe base64
  s = s.replace(/-/g, "+").replace(/_/g, "/");

  // pad to multiple of 4
  if (s.length % 4 !== 0) s += "=".repeat(4 - (s.length % 4));

  // must be only base64 alphabet with up to two '=' at end
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(s)) return false;

  // length must be multiple of 4
  if (s.length % 4 !== 0) return false;

  return true;
};

export const base64ToHex = (base64String: string): string => {
  if (!isBase64Strict(base64String)) {
    return String(base64String ?? "");
  }

  // normalize again to decode
  let s = String(base64String)
    .trim()
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  if (s.length % 4 !== 0) s += "=".repeat(4 - (s.length % 4));

  const binary = atob(s);
  const hex = Array.from(binary)
    .map(ch =>
      ch
        .charCodeAt(0)
        .toString(16)
        .padStart(2, "0")
    )
    .join("");

  return "0x" + hex;
};
/* eslint-disable */
export const groupBy = (items: Api.AnyItem, key: string, nestedKey?: any) => {
  if (nestedKey) {
    return items.reduce((rv: any, x: any) => {
      (rv[x[key][nestedKey]] = rv[x[key][nestedKey]] || []).push(x);
      return rv;
    }, {});
  }

  return items.reduce((rv: any, x: any) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

/**
 * Parse a semicolon-delimited config string "default;min;max" into a numeric tuple.
 */
export const parseConfig = (value?: string): [number, number, number] => {
  const parts = (value || "0;0;0").split(";");
  return [
    parseInt(parts[0] ?? "0", 10) || 0,
    parseInt(parts[1] ?? "0", 10) || 0,
    parseInt(parts[2] ?? "0", 10) || 0,
  ];
};

/**
 * To remove first n parts of enumName e.g. IO_CPU_CPUNAME -> CPUNAME
 */
export const parseEnumStart = (name: string, nOfUnderscoresToRemove: number): string =>
  name
    .split("_")
    .splice(nOfUnderscoresToRemove, name.split("_").length)
    .join("_");

export const driveVersionFilter = (
  collections: Api.DriveParameter[],
  versionId: number
): Api.DriveParameter[] => {
  let filterByVersion = [];
  if (versionId === -1) {
    return collections;
  } else {
    filterByVersion = collections.filter((element: Api.DriveParameter) =>
      element.driveParameterBindings.some(
        (subElement: Api.DriveParameterBinding): boolean => subElement.driveVersion.id === versionId
      )
    );
    return filterByVersion;
  }
};

export const escalatorVersionFilter = (
  collections: Api.EscalatorParameter[],
  versionId: number
): Api.EscalatorParameter[] => {
  let filterByVersion = [];
  if (versionId === -1) {
    return collections;
  } else {
    filterByVersion = collections.filter((element: Api.EscalatorParameter) =>
      element.escalatorParameterBindings.some(
        (subElement: Api.EscalatorParameterBinding): boolean => subElement.escalatorVersion.id === versionId
      )
    );
    return filterByVersion;
  }
};

export const escalatorFaultVersionFilter = (
  collections: Api.EscalatorFault[],
  versionId: number
): Api.EscalatorFault[] => {
  if (versionId === -1) {
    return collections;
  } else {
    return collections.filter((element: Api.EscalatorFault) =>
      (element.escalatorFaultBindings || []).some(
        (subElement: Api.EscalatorFaultBinding): boolean => subElement.escalatorVersion.id === versionId
      )
    );
  }
};

export const doorVersionFilter = (
  collections: Api.DoorParameter[],
  versionId: number
): Api.DoorParameter[] => {
  let filterByVersion = [];
  if (versionId === -1) {
    return collections;
  } else {
    filterByVersion = collections.filter((element: Api.DoorParameter) =>
      element.doorParameterBindings.some(
        (subElement: Api.DoorParameterBinding): boolean => subElement.doorVersion.id === versionId
      )
    );
    return filterByVersion;
  }
};

export const liftVersionFilter = (
  collections: Api.LiftParameter[],
  versionId: number
): Api.LiftParameter[] => {
  let filterByVersion = [];
  if (versionId === -1) {
    return collections;
  } else {
    filterByVersion = collections.filter((element: any) =>
      element.liftParameterBindings.some(
        (subElement: any) => subElement.kceVersion.id === versionId
      )
    );
    return filterByVersion;
  }
};

export function getIndexedValue(propertyPathArray: string[], row: any): string[] | null {
  if (propertyPathArray.length === 1) {
    return row[propertyPathArray[0]];
  } else if (propertyPathArray.length === 2) {
    const intermediate = row[propertyPathArray[0]];
    if (intermediate === null || intermediate === undefined) return null;
    return intermediate[propertyPathArray[1]];
  }
  return null;
}

export default function formatDateString(date: string): string {
  return moment(date).format("YYYY-MM-DD HH:mm:ss");
}

export function urlToDocumentTitle(url: string): string {
  return (
    "KCE Tools - " +
    url
      .split("/")
      .filter(n => n)
      .map(i => i.charAt(0).toUpperCase() + i.slice(1).replaceAll("-", " "))
      .join(" - ")
      .replace("Io ", "IO ")
      .replace("Apm", "APM")
      .replace("Lio", "LIO")
      .replace("Aadgroups", "AAD groups")
  );
}

export function getDeck(network: Api.BaseEnumeration | undefined): number | null {
  switch (network?.enumName) {
    case "NET_LON_CAR1":
      return 0;
    case "NET_LON_CAR2":
      return 1;
    case "NET_NONE":
    case "NET_ETHERNET":
    case "NET_TTS1":
    case "NET_TTS2":
    case "NET_LON_SHAFT":
    case "NET_LON_MR":
      return 3;
    default:
      return null;
  }
}

// Converts unformatted drive parameter value to formatted
export function convertFormattedValueString(rawValue: number, decimals: number): string {
  return (rawValue / Math.pow(10, decimals)).toFixed(decimals);
}
